import { action, makeAutoObservable, observable } from "mobx";
import { createContext } from "react";

import { TableTag } from "../interfaces/base";
import { OperatorGamePopulated, TicketTemplate } from "../shared/interfaces";
import { GameStatus } from "../interfaces/game";
import { NotificationServiceInstance } from "./notification";
import {
  addTemplateRequest,
  backfillTemplateTeamIdsRequest,
  deleteTemplateRequest,
  getTemplateByIdRequest,
  getTemplatesRequest,
  updateTemplateRequest,
} from "../axios/routes/templates";
import { SessionServiceInstance } from "./session";
import { getCurrentTeamName } from "../utils/domainAnalysis";

export interface GamePopulatedWithTag extends OperatorGamePopulated, TableTag {
  status: GameStatus,
}

class GameService {
  @observable templates: TicketTemplate[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  @action public fetchTemplates = async (teamId?: string) => {
    await this.fetchTemplatesWithFilter(
      teamId ? { teamId } : { useSubdomain: true },
    );
  }

  /** Load templates for a specific home team (e.g. game graphics on global operator host). */
  @action public fetchTemplatesForTeam = async (teamId: string, hostname?: string) => {
    await this.fetchTemplatesWithFilter({ teamId, hostname });
  }

  /**
   * @param filter.teamId — templates for this team (+ globals)
   * @param filter.all — every template (operator templates page)
   * @param filter.useSubdomain — hostname slug when no teamId/all
   */
  @action public fetchTemplatesWithFilter = async (filter: {
    teamId?: string;
    all?: boolean;
    useSubdomain?: boolean;
    hostname?: string;
  }) => {
    try {
      const slug = (filter.hostname || getCurrentTeamName() || "").trim().toLowerCase();
      if (filter.all) {
        this.templates = await getTemplatesRequest(slug, undefined, true);
      } else if (filter.teamId) {
        this.templates = await getTemplatesRequest(slug, filter.teamId);
      } else {
        this.templates = await getTemplatesRequest(slug);
      }
    } catch (err) {
      NotificationServiceInstance.showErrorNotification(err.message);
    }
  }

  @action public runTemplateTeamIdBackfill = async (): Promise<void> => {
    if (!SessionServiceInstance.isSuperAdmin) return;
    try {
      const result = await backfillTemplateTeamIdsRequest();
      if (result.updated > 0) {
        NotificationServiceInstance.showInfoNotification(
          `Linked ${result.updated} ticket template(s) to teams`,
        );
      }
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification(
        "runTemplateTeamIdBackfill",
        (err as Error).message,
      );
    }
  }

  public fetchTemplateById = async (templateId: string): Promise<TicketTemplate|null> => {
    try {
      return await getTemplateByIdRequest(templateId);
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchTemplateById()", err.message);
      return null;
    }
  }

  public deleteTemplateById = async (templateId: string): Promise<boolean|null> => {
    try {
      await deleteTemplateRequest(templateId);
      const index = this.templates.findIndex((g) => g._id === templateId);
      if (index !== -1) {
        this.templates.splice(index, 1);
      }

      return true;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchTemplateById()", err.message);
      return null;
    }
  }

  public createTemplate = async (request: FormData): Promise<TicketTemplate | null> => {
    try {
      const { _id } = await addTemplateRequest(request);
      if (!_id) {
        NotificationServiceInstance.showUnexpectedErrorNotification("createTemplate()", "Cannot create template");
        return null;
      }

      const newTemplate = await getTemplateByIdRequest(_id);
      this.templates.unshift({
        ...newTemplate,
      });

      return newTemplate;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("createTemplate()", err.message);
      return null;
    }
  }

  public updateTemplate = async (request: TicketTemplate, _id: string): Promise<boolean> => {
    try {
      if (await updateTemplateRequest(request, _id)) {
        const updatedTemplate = await getTemplateByIdRequest(_id);

        const index = this.templates.findIndex((g) => g._id === _id);
        if (index !== -1) {
          this.templates.splice(index, 1, {
            ...updatedTemplate,
          });
        }
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateTemplate()", err.message);
      return false;
    }
  }

  public deleteTemplate = async (_id: string) => {
    try {
      if (await deleteTemplateRequest(_id)) {
        const index = this.templates.findIndex((g) => g._id === _id);
        if (index !== -1) {
          this.templates.splice(index, 1);
        }
      }
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("deleteTemplate()", err.message);
    }
  }
}

export const TemplateServiceInstance = new GameService();

export const TemplateServiceContext = createContext(TemplateServiceInstance);
